import React, { useState, useEffect } from "react";
import "@/App.css";
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { generateProductSlug } from "./utils";
import {
  ShoppingCart, ChevronRight, ChevronLeft, Phone, ArrowRight,
  Truck, Shield, Headphones, RefreshCw, Award, CreditCard,
  Droplets, Zap, Lightbulb, Wrench, Pipette, Paintbrush, Hammer,
  Minus, Plus, Trash2, Package, Grid, List, Check, AlertCircle, Loader2, X,
  FileText, Settings, LayoutDashboard, MapPin, Mail, MessageSquare, Send,
  BarChart2, Star, User, Clock, Users, DollarSign, AlertTriangle, Upload, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";

import { AppProvider, useApp } from "@/context/AppContext";
import Header from "@/components/layout/Header";
import BottomNav from "@/components/layout/BottomNav";
import Footer from "@/components/layout/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ChatWidget from "@/components/ChatWidget";
import ProductCard from "@/components/ProductCard";
import CategorySidebar from "@/components/CategorySidebar";
import ProductDetailPage from "@/pages/ProductDetailPage";
import AccountPage, { OrderDetailPage } from "@/pages/AccountPage";
import WishlistPage from "@/pages/WishlistPage";
import VerifyAccountPage from "@/pages/VerifyAccountPage";
import { AdminProducts, AdminOrders, AdminCategories, AdminSettings, AdminCustomers, AdminFeaturedProducts, AdminQuotations } from "@/pages/Admin";
import { AdminOffers } from "@/pages/AdminOffers";
import AdminLayout from "@/components/AdminLayout";
import ERPDashboard from "@/pages/ERPDashboard";
import InvoiceManagement from "@/pages/InvoiceManagement";
import ReportsModule from "@/pages/ReportsModule";
import ERPSettings from "@/pages/ERPSettings";
import CustomerERPView from "@/pages/CustomerERPView";
import EnquiriesPage from "@/pages/EnquiriesPage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import IndustriesPage from "@/pages/IndustriesPage";
import FAQPage from "@/pages/FAQPage";
import ContactPage from "@/pages/ContactPage";
import LandingPage from "@/pages/LandingPage";
import AdminHelp from "@/pages/AdminHelp";
import AdminChat from "@/pages/AdminChat";
import AdminBulkUpload from "@/pages/AdminBulkUpload";
import Price from "@/components/Price";
import Meta from "@/components/Meta";
import CatalogPage from "@/pages/CatalogPage";
import { auth } from "./firebase-config";
import { RecaptchaVerifier, signInWithPhoneNumber, GoogleAuthProvider, signInWithPopup } from "firebase/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Droplets: Droplets, Zap: Zap, Lightbulb: Lightbulb, Shield: Shield,
  Wrench: Wrench, Pipette: Pipette, Paintbrush: Paintbrush, Hammer: Hammer
};

// HomePage
const HomePage = () => {
  const { categories, allCategoriesFlat, settings } = useApp();
  const showPrices = settings?.show_prices !== false;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [allProducts, setAllProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellingProducts, setBestSellingProducts] = useState([]);
  const [activeProductTab, setActiveProductTab] = useState('All Products');
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [allCategoriesProducts, setAllCategoriesProducts] = useState({});
  const [promoBanners, setPromoBanners] = useState([]);
  const navigate = useNavigate();

  // Finetools-style product tabs
  const productTabs = ['All Products', 'Featured', 'New Arrivals', 'Best Selling'];

  useEffect(() => {
    if (categories && categories.length > 0) {
      const fetchAllCategoryProducts = async () => {
        try {
          const results = await Promise.allSettled(
            categories.map(cat => axios.get(`${API}/products?category_id=${cat.id}&limit=5`))
          );
          const newMap = {};
          categories.forEach((cat, idx) => {
            const res = results[idx];
            if (res.status === 'fulfilled' && res.value.data.products) {
              newMap[cat.id] = res.value.data.products;
            } else {
              newMap[cat.id] = [];
            }
          });
          setAllCategoriesProducts(newMap);
        } catch (error) {
          console.error("Error fetching category products:", error);
        }
      };
      fetchAllCategoryProducts();
    }
  }, [categories, API]);

  const defaultHeroSlides = [
    { image: "/media/banner_shower.jpg",      title: "High Performance Products", subtitle: "Designed for Durability, Hygiene and Comfort" },
    { image: "/media/banner_categories1.jpg", title: "Sanitaryware · Lighting · Paints", subtitle: "Shop by Category — Premium brands for every space" },
    { image: "/media/banner_categories2.jpg", title: "Electrical · Tools · Safety", subtitle: "Trusted by professionals across the UAE" },
    { image: "/media/banner_dubai.jpg",        title: "Grand Palace General Trading", subtitle: "Your partner in construction & trading — #WeAreUAE" },
  ];

  const [heroSlides, setHeroSlides] = useState(defaultHeroSlides);

  useEffect(() => {
    // Hero banners are managed in the admin panel (Settings → Hero Banners,
    // stored in settings.hero_slides). The static defaultHeroSlides are only
    // a fallback while settings load or when no banners are configured.
    const adminSlides = (settings?.hero_slides || []).filter(s => s && s.image);
    if (adminSlides.length > 0) {
      setHeroSlides(adminSlides);
      setCurrentSlide(0);
    } else {
      setHeroSlides(defaultHeroSlides);
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const fetchPromoBanners = async () => {
      try {
        const res = await axios.get(`${API}/promo-banners`);
        if (res.data && Array.isArray(res.data.banners) && res.data.banners.length > 0) {
          setPromoBanners(res.data.banners);
        }
      } catch (e) { console.error('Failed to fetch promo banners'); }
    };
    fetchPromoBanners();
  }, []);

  useEffect(() => {
    if (heroSlides.length === 0) return;
    const timer = setInterval(() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length), 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const features = [
    { icon: Truck, title: "Free Delivery", desc: <>Free shipping on orders above <Price amount={settings?.free_shipping_threshold || 1000} size="sm" showIcon={true} /></> },
    { icon: Shield, title: "Secure Payment", desc: "Multiple secure payment options" },
    { icon: Headphones, title: "24/7 Support", desc: "Dedicated customer support team" },
    { icon: RefreshCw, title: "Easy Returns", desc: "30-day hassle-free return policy" },
    { icon: Award, title: "Quality Assured", desc: "All products are quality checked" },
    { icon: CreditCard, title: "Flexible Payment", desc: "Pay in installments with 0% interest" }
  ];

  useEffect(() => {
    const fetchHomeProducts = async () => {
      // Retry up to 5 times — backend may still be starting in Docker
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          const [allRes, featuredRes, newArrivalsRes, bestSellingRes] = await Promise.all([
            axios.get(`${API}/products?limit=5`),
            axios.get(`${API}/products?featured=true&limit=5`),
            axios.get(`${API}/products?sort_by=created_at&sort_order=desc&limit=5`),
            axios.get(`${API}/products?sort_by=price&sort_order=desc&limit=5`)
          ]);
          
          setAllProducts(allRes.data.products || []);
          setFeaturedProducts(featuredRes.data.products || []);
          setNewArrivals(newArrivalsRes.data.products || []);
          setBestSellingProducts(bestSellingRes.data.products || []);
          return; // Success
        } catch (e) {
          if (attempt < 5) {
            console.warn(`Home products fetch attempt ${attempt} failed. Retrying in 2s...`);
            await new Promise(r => setTimeout(r, 2000));
          } else {
            console.warn('Backend unavailable. Home products could not be loaded.');
          }
        }
      }
    };
    fetchHomeProducts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeProductTab === 'All Products') {
      setDisplayedProducts(allProducts);
    } else if (activeProductTab === 'Featured') {
      setDisplayedProducts(featuredProducts);
    } else if (activeProductTab === 'New Arrivals') {
      setDisplayedProducts(newArrivals);
    } else if (activeProductTab === 'Best Selling') {
      setDisplayedProducts(bestSellingProducts);
    }
  }, [activeProductTab, allProducts, featuredProducts, newArrivals, bestSellingProducts]);

  return (
    <main className="flex-1">
      <Meta title="Home" />
      {/* Finetools Layout: Sidebar + Slider */}
      <div className="container mx-auto px-4 mt-6 lg:flex lg:items-start lg:gap-6 mb-12">
        {/* Desktop Category Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <CategorySidebar
            categories={categories}
            allCategoriesFlat={allCategoriesFlat || categories}
            categoryId={null}
            onCategoryChange={(catId) => {
              if(catId) navigate(`/products?category=${catId}`);
              else navigate('/products');
            }}
          />
        </aside>

        {/* Main Content Area (Slider + Offers) */}
        <div className="flex-1 w-full min-w-0 flex flex-col gap-4">
          {/* Hero Section */}
          <section className="relative w-full overflow-hidden rounded-xl shadow-sm group bg-white flex-shrink-0" data-testid="hero-section">
            {/* Natural-height placeholder — drives container height to match image aspect ratio */}
            {heroSlides.length > 0 ? (
              <img
                src={heroSlides[0].image}
                alt=""
                className="w-full h-auto block invisible"
                aria-hidden="true"
              />
            ) : (
              <div className="w-full" style={{ paddingBottom: '39%' }}></div>
            )}
            
            {heroSlides.map((slide, index) => (
              <div key={index} className={`absolute inset-0 transition-all duration-700 ease-in-out ${index === currentSlide ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0 pointer-events-none'}`}>
                <img 
                  src={slide.image.startsWith('/api') ? `${window.location.origin}${slide.image}` : slide.image} 
                  alt={slide.title || 'Hero Banner'} 
                  className="w-full h-full object-contain cursor-pointer" 
                  onClick={() => navigate('/products')}
                />
              </div>
            ))}
            {heroSlides.length > 1 && (
              <>
                <button onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white hover:text-[#0c1b44] transition-colors flex items-center justify-center z-20 opacity-0 group-hover:opacity-100"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white hover:text-[#0c1b44] transition-colors flex items-center justify-center z-20 opacity-0 group-hover:opacity-100"><ChevronRight className="h-5 w-5" /></button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
                  {heroSlides.map((_, i) => (<button key={i} onClick={() => setCurrentSlide(i)} className={`rounded-full transition-all ${i === currentSlide ? 'w-8 h-2.5 bg-[#0c1b44]' : 'w-2.5 h-2.5 bg-white/60 hover:bg-white'}`} />))}
                </div>
              </>
            )}
          </section>

          {/* New Offer Display Text area below slider */}
          {settings?.special_offer_enabled !== false && (
            <div className="w-full bg-gradient-to-r from-[#0c1b44] to-[#1a3673] rounded-xl p-4 sm:p-6 text-white shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
               <div className="flex items-center gap-4">
                 <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                   <Zap className="h-6 w-6 text-[#0c1b44]" />
                 </div>
                 <div>
                   <h3 className="font-bold text-lg sm:text-xl">{settings?.special_offer_title || 'Special Weekly Offers'}</h3>
                   <p className="text-white/80 text-sm">{settings?.special_offer_desc || 'Get up to 30% off on premium sanitaryware & electrical tools'}</p>
                 </div>
               </div>
               <Button className="bg-amber-400 hover:bg-amber-500 text-[#0c1b44] font-bold whitespace-nowrap" onClick={() => navigate('/products?offers=true')}>
                 Explore Offers <ArrowRight className="ml-2 w-4 h-4" />
               </Button>
            </div>
          )}

          {/* Promotional Banners — dynamic from admin settings */}
          <div className="mt-auto pt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {promoBanners.map((banner, idx) => {
              const cat = allCategoriesFlat.find(c => c.name === banner.category_name);
              return (
                <div
                  key={idx}
                  className="relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg border border-gray-100 group cursor-pointer transition-all duration-300 flex flex-col"
                  onClick={() => navigate('/products' + (cat ? `?category=${cat.id}` : ''))}
                >
                  <div className="w-full aspect-[4/3] relative overflow-hidden flex-shrink-0">
                    <img
                      src={banner.image.startsWith('/api') ? `${window.location.origin}${banner.image}` : banner.image}
                      alt={banner.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-4 right-4 z-10 flex flex-col justify-end">
                      {banner.badge && (
                        <span className="text-[10px] text-amber-400 uppercase font-extrabold tracking-wider drop-shadow-md mb-1 block line-clamp-1">
                          {banner.badge}
                        </span>
                      )}
                      <h3 className="font-bold text-white text-sm sm:text-base leading-snug drop-shadow-md line-clamp-2">
                        {banner.title}
                      </h3>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 bg-gray-50 flex-1 flex justify-between items-center group-hover:bg-white transition-colors">
                    <span className="text-xs sm:text-sm font-semibold text-gray-600 group-hover:text-navy transition-colors line-clamp-1">
                      {banner.label || "Shop Now"}
                    </span>
                    <span className="w-6 h-6 rounded-full bg-white shadow-sm group-hover:bg-amber-400 group-hover:text-navy flex items-center justify-center transition-colors flex-shrink-0">
                      <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Product Highlight Tabs Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          
          {/* Finetools Style Header & Tabs */}
          <div className="flex flex-col md:flex-row justify-between items-end mb-6 border-b border-gray-200">
            <div className="flex overflow-x-auto no-scrollbar gap-6 sm:gap-10 pb-1 w-full justify-start items-center">
              {productTabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveProductTab(tab)}
                  className={`text-base sm:text-xl whitespace-nowrap relative pb-3 transition-colors flex-shrink-0 ${
                    activeProductTab === tab 
                      ? 'text-navy font-bold border-b-[3px] border-[#38b6ff]' 
                      : 'text-gray-500 font-medium hover:text-navy border-b-[3px] border-transparent'
                  }`}
                >
                  {tab}
                  {activeProductTab === tab && (
                    <div className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-2 h-2 sm:w-3 sm:h-3 bg-[#38b6ff] rounded-full"></div>
                  )}
                </button>
              ))}
            </div>
            <Link to="/products" className="hidden md:flex items-center gap-2 text-[#0c1b44] font-semibold pb-3 hover:underline whitespace-nowrap flex-shrink-0">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Product Grid */}
          {displayedProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4">
              {displayedProducts.slice(0, 5).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              {/* 6th Card: View All */}
              <div 
                onClick={() => navigate('/products')}
                className="group relative bg-[#f8fafc] rounded-xl border border-dashed border-gray-200 hover:border-[#0c1b44] hover:bg-[#0c1b44]/5 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 min-h-[250px]"
              >
                <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <ArrowRight className="h-5 w-5 text-[#0c1b44]" />
                </div>
                <span className="font-semibold text-sm text-[#0c1b44]">View All</span>
                <span className="text-xs text-gray-400 mt-1">{activeProductTab}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-muted/30 rounded-xl border border-dashed border-gray-300">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No products found in this category yet.</p>
            </div>
          )}
          
          <div className="mt-8 text-center md:hidden">
            <Link to="/products" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0c1b44] text-white rounded font-medium whitespace-nowrap flex-shrink-0">
              View All Products <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Category Sections (One by One) */}
      <section className="py-12 bg-gray-50 space-y-16">
        <div className="container mx-auto px-4">
          {categories.map(cat => {
            const catProducts = allCategoriesProducts[cat.id] || [];
            if (catProducts.length === 0) return null; // Only show categories that have products fetched

            return (
              <div key={cat.id} className="mb-12 last:mb-0">
                <div className="flex flex-col md:flex-row justify-between items-end mb-6 border-b border-gray-200 pb-2">
                  <h2 className="text-2xl font-bold text-navy relative pb-2">
                    {cat.name}
                    <div className="absolute bottom-[-10px] left-0 w-16 h-[3px] bg-[#38b6ff]"></div>
                  </h2>
                  <Link to={`/products?category=${cat.id}`} className="hidden md:flex items-center gap-2 text-[#0c1b44] font-semibold hover:underline mt-4 md:mt-0">
                    View All {cat.name} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4">
                  {catProducts.slice(0, 5).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                  
                  {/* 6th Card: View All */}
                  <div 
                    onClick={() => navigate('/products?category=' + cat.id)}
                    className="group relative bg-[#f8fafc] rounded-xl border border-dashed border-gray-200 hover:border-[#0c1b44] hover:bg-[#0c1b44]/5 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 min-h-[250px]"
                  >
                    <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <ArrowRight className="h-5 w-5 text-[#0c1b44]" />
                    </div>
                    <span className="font-semibold text-sm text-[#0c1b44]">View All</span>
                    <span className="text-xs text-gray-400 mt-1 text-center px-2 line-clamp-1">in {cat.name}</span>
                  </div>
                </div>

                <div className="mt-6 text-center md:hidden">
                  <Link to={`/products?category=${cat.id}`} className="inline-flex items-center justify-center gap-2 px-6 py-2 border border-[#0c1b44] text-[#0c1b44] hover:bg-[#0c1b44] hover:text-white transition-colors rounded font-medium">
                    View All <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Brand Scroll — Auto-scrolling marquee of 12 partner brands */}
      <section className="py-8 bg-white border-b border-gray-100 border-t overflow-hidden">
        <div className="container mx-auto px-4 mb-4">
          <p className="text-center text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">Trusted Brands We Deal With</p>
        </div>
        <div className="relative">
          <div className="flex animate-marquee gap-12 sm:gap-16 items-center whitespace-nowrap">
            {[
              { name: "JAQUAR", logo: "/brands/jaquar.png" },
              { name: "DOCOL", logo: "/brands/docol.svg" },
              { name: "OSRAM", logo: "/brands/osram.svg" },
              { name: "MK", logo: "/brands/mk.svg" },
              { name: "DUCAB", logo: "/brands/ducab.png" },
              { name: "JOTUN", logo: "/brands/jotun.svg" },
              { name: "RAK CERAMICS", logo: "/brands/rak.svg" },
              { name: "GEZE", logo: "/brands/geze.svg" },
              { name: "USG BORAL", logo: "/brands/usg_boral.svg" },
              { name: "HIKVISION", logo: "/brands/hikvision.svg" },
              { name: "GRANLACE", logo: null },
              { name: "DORMA", logo: "/brands/dorma.svg" },
              // Duplicate for seamless loop
              { name: "JAQUAR", logo: "/brands/jaquar.png" },
              { name: "DOCOL", logo: "/brands/docol.svg" },
              { name: "OSRAM", logo: "/brands/osram.svg" },
              { name: "MK", logo: "/brands/mk.svg" },
              { name: "DUCAB", logo: "/brands/ducab.png" },
              { name: "JOTUN", logo: "/brands/jotun.svg" },
              { name: "RAK CERAMICS", logo: "/brands/rak.svg" },
              { name: "GEZE", logo: "/brands/geze.svg" },
              { name: "USG BORAL", logo: "/brands/usg_boral.svg" },
              { name: "HIKVISION", logo: "/brands/hikvision.svg" },
              { name: "GRANLACE", logo: null },
              { name: "DORMA", logo: "/brands/dorma.svg" },
            ].map((brand, i) => (
              <div key={i} className="flex-shrink-0 flex items-center justify-center h-16 cursor-pointer group">
                {brand.logo ? (
                  <img 
                    src={brand.logo} 
                    alt={brand.name}
                    className="h-8 md:h-10 max-w-[140px] object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300 mix-blend-multiply"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <span className={`${brand.logo ? 'hidden' : 'block'} text-[#0c1b44] font-black tracking-wider text-xl md:text-2xl opacity-50 group-hover:opacity-100 transition-all`}>
                  {brand.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Column Lists (New Arrivals | Featured | Best Selling) */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">

            {(() => {
              // Use the distinct lists from the API for each column (limit to 4 items each)
              const columnData = [
                { title: 'New Arrivals', products: newArrivals.slice(0, 4) },
                { title: 'Featured Products', products: featuredProducts.slice(0, 4) },
                { title: 'Best Selling', products: bestSellingProducts.slice(0, 4) },
              ];

              return columnData.map((col, colIdx) => (
                <div key={colIdx}>
                  <div className="flex items-center justify-between mb-6 border-b border-gray-200">
                    <h3 className="text-gray-700 font-bold pb-2 relative text-lg">
                      {col.title}
                      <div className="absolute bottom-[-1.5px] left-0 w-full h-[3px] bg-yellow-400"></div>
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5">
                    {col.products.length > 0 ? col.products.map((product, i) => (
                      <Link key={i} to={`/products/${generateProductSlug(product.name, product.id, product.slug)}`} className="flex gap-4 group">
                        <div className="w-20 h-20 flex-shrink-0 bg-gray-50 border border-gray-100 p-1.5 flex items-center justify-center rounded-lg overflow-hidden">
                          <img
                            src={product.images?.[0] || ''}
                            alt={product.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="flex flex-col pt-1 min-w-0">
                          <h4 className="text-sm font-bold text-[#0c1b44] group-hover:text-blue-600 transition-colors leading-snug line-clamp-2 mb-1">{product.name}</h4>
                          {product.price > 1 && (
                            <p className="text-gray-500 text-sm"><Price amount={product.price} size="sm" showIcon={true} /></p>
                          )}
                        </div>
                      </Link>
                    )) : (
                      <p className="text-sm text-gray-400">No products yet</p>
                    )}
                  </div>
                </div>
              ));
            })()}

          </div>
        </div>
      </section>

      {/* Location Map */}
      <section className="bg-white">
        <div className="w-full h-80 md:h-96 relative border-y border-gray-200">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3607.728522307559!2d55.3825177!3d25.2764152!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d56e1c673b9%3A0xdb1f4d0ce5bc429d!2sGrand%20Palace%20General%20Trading%20L.L.C!5e0!3m2!1sen!2sae!4v1709477382949!5m2!1sen!2sae"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location"
            className="w-full h-full grayscale-[50%] hover:grayscale-0 transition-all duration-700 opacity-90 hover:opacity-100"
          ></iframe>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-navy" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0c1b44]/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-lg text-white/80 mb-10">Get in touch for personalized quotes and expert advice.</p>
          <div className="flex justify-center gap-4">
            <Button onClick={() => navigate('/products')} className="bg-gradient-to-r from-gold to-gold-light text-white font-bold h-14 px-10 text-lg">Browse Products <ArrowRight className="ml-2 h-5 w-5" /></Button>
            <Button onClick={() => navigate('/contact')} variant="outline" className="border-white/30 text-white h-14 px-10 text-lg hover:bg-white hover:text-navy-dark"><Mail className="mr-2 h-5 w-5" /> Get Quote</Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-navy">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {features.map((f, i) => (
              <Card key={i} className="bg-navy-light border-navy-light/50 text-center hover:bg-[#0c1b44]/10 transition-colors">
                <CardContent className="p-4">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gold/20 flex items-center justify-center"><f.icon className="h-6 w-6 text-gold" /></div>
                  <h3 className="font-semibold text-white mb-1 text-sm">{f.title}</h3>
                  <p className="text-xs text-white/70">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

// Products Page
const ProductsPage = () => {
  const { categories, allCategoriesFlat } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);   // true only on first load
  const [fetching, setFetching] = useState(false); // true on subsequent fetches (no skeleton)
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(40);
  const [viewMode, setViewMode] = useState('grid');
  const categoryId = searchParams.get('category');
  const search = searchParams.get('search');
  const sortBy = searchParams.get('sort') || 'name';
  const isOffers = searchParams.get('offers') === 'true';
  const isFirstLoad = React.useRef(true);

  useEffect(() => {
    const fetchProducts = async () => {
      // First load → show skeleton; subsequent → overlay only (no flash)
      if (isFirstLoad.current) {
        setLoading(true);
      } else {
        setFetching(true);
      }
      try {
        const params = new URLSearchParams();
        if (categoryId) params.append('category_id', categoryId);
        if (search) params.append('search', search);
        if (isOffers) params.append('is_weekly_offer', 'true');

        const effectiveSortBy = sortBy === 'price_asc' || sortBy === 'price_desc' ? 'price' : 'name';
        const effectiveSortOrder = sortBy === 'price_asc' ? 'asc' : (sortBy === 'price_desc' ? 'desc' : 'asc');

        params.append('sort_by', effectiveSortBy);
        params.append('sort_order', effectiveSortOrder);
        params.append('limit', limit);
        const res = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(res.data.products || []);
        setTotal(res.data.total || 0);
      } catch (e) { toast.error('Failed to load products'); }
      setLoading(false);
      setFetching(false);
      isFirstLoad.current = false;
    };
    fetchProducts();
  }, [categoryId, search, sortBy, isOffers, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset limit when category or search changes
  useEffect(() => {
    setLimit(40);
  }, [categoryId, search, isOffers]);

  const selectedCategory = allCategoriesFlat.find(c => c.id === categoryId);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-[#0c1b44]">Home</Link><ChevronRight className="h-4 w-4" /><span className="text-navy">Products</span>
          {selectedCategory && <><ChevronRight className="h-4 w-4" /><span className="text-[#0c1b44]">{selectedCategory.name}</span></>}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="w-full lg:w-64">
            <CategorySidebar
              categories={categories}
              allCategoriesFlat={allCategoriesFlat}
              categoryId={null}
              onCategoryChange={(catId) => catId ? setSearchParams({ category: catId }) : setSearchParams({})}
            />
          </aside>
          <div className="flex-1">
            <div className="flex justify-between items-center gap-4 mb-6">
              <div>
                <Meta 
                  title={isOffers ? 'Special Weekly Offers' : (search ? `Search: "${search}"` : selectedCategory?.name || 'All Products')} 
                  description={`Shop ${selectedCategory?.name || 'our wide range of products'} at Grand Palace General Trading.`}
                  url={`https://gpgt.ae/products${categoryId ? `?category=${categoryId}` : ''}`}
                />
                <h1 className="text-2xl font-bold text-navy">
                  {isOffers ? 'Special Weekly Offers' : (search ? `Search: "${search}"` : selectedCategory?.name || 'All Products')}
                </h1>
                <p className="text-muted-foreground">{fetching ? 'Loading...' : `${total} products found`}</p>
                
                {/* Subcategory Pills */}
                {selectedCategory && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {allCategoriesFlat.filter(c => c.parent_id === selectedCategory.id).length > 0 && (
                      <div className="flex flex-wrap gap-2 w-full">
                        {allCategoriesFlat.filter(c => c.parent_id === selectedCategory.id).map(sub => (
                          <button
                            key={sub.id}
                            onClick={() => setSearchParams({ category: sub.id })}
                            className="px-4 py-1.5 rounded-full text-sm font-medium border border-[#0c1b44]/20 bg-white text-[#0c1b44] hover:bg-[#0c1b44] hover:text-white transition-colors"
                          >
                            {sub.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedCategory.parent_id && (
                      <button
                        onClick={() => setSearchParams({ category: selectedCategory.parent_id })}
                        className="px-4 py-1.5 rounded-full text-sm font-medium border border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
                      >
                        <ChevronLeft className="h-3 w-3" /> Back to Parent
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex border rounded-lg overflow-hidden">
                  <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-[#0c1b44] text-white hover:bg-[#1a2d64]' : ''}`}><Grid className="h-4 w-4" /></button>
                  <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-[#0c1b44] text-white hover:bg-[#1a2d64]' : ''}`}><List className="h-4 w-4" /></button>
                </div>
              </div>
            </div>

            {/* First load skeleton */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => <Card key={i}><div className="aspect-square skeleton" /><CardContent className="p-4"><div className="h-4 w-20 skeleton rounded mb-2" /><div className="h-5 w-full skeleton rounded" /></CardContent></Card>)}
              </div>
            ) : products.length === 0 && !fetching ? (
              <div className="text-center py-12"><Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" /><h3 className="text-lg font-semibold">No products found</h3></div>
            ) : (
              <div className="relative">
                {/* Subtle loading overlay — keeps existing products visible, no flash */}
                {fetching && (
                  <div className="absolute inset-0 z-10 bg-white/50 flex items-center justify-center rounded-xl" style={{ backdropFilter: 'blur(1px)' }}>
                    <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-md text-sm text-[#0c1b44] font-medium">
                      <div className="w-4 h-4 border-2 border-[#0c1b44] border-t-transparent rounded-full animate-spin" />
                      Loading...
                    </div>
                  </div>
                )}
                <div className={viewMode === 'grid' ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" : "flex flex-col gap-4"}>
                  {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} viewMode={viewMode} />)}
                </div>
                
                {products.length < total && (
                  <div className="mt-10 text-center">
                    <Button 
                      variant="outline" 
                      className="border-[#0c1b44] text-[#0c1b44] hover:bg-[#0c1b44] hover:text-white px-8 h-12 text-base"
                      onClick={() => setLimit(prev => prev + 40)}
                      disabled={fetching}
                    >
                      {fetching ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Load More Products'}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-3">
                      Showing {products.length} of {total} products
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Product Detail Page
// ProductDetailPage is now imported from @/pages/ProductDetailPage.js

// Cart Page
const CartPage = () => {
  const { cart, updateCartItem, removeFromCart, settings } = useApp();
  const navigate = useNavigate();
  const showPrices = settings?.show_prices !== false;
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    if (cart.items?.length > 0) {
      const categoryIds = [...new Set(cart.items.map(item => item.product?.category_id).filter(Boolean))];
      if (categoryIds.length > 0) {
        axios.get(`${API}/products?category_id=${categoryIds[0]}&limit=6`)
          .then(async res => {
            const cartProductIds = new Set(cart.items.map(i => i.product_id));
            let filtered = res.data.products?.filter(p => !cartProductIds.has(p.id)) || [];
            
            if (filtered.length === 0) {
               // Fallback if category has no other products
               try {
                 const fallbackRes = await axios.get(`${API}/products?limit=6`);
                 filtered = fallbackRes.data.products?.filter(p => !cartProductIds.has(p.id)) || [];
               } catch(e) { console.error(e); }
            }
            setRelatedProducts(filtered);
          })
          .catch(e => console.error(e));
      }
    } else {
      setRelatedProducts([]);
    }
  }, [cart.items]);

  if (!cart.items?.length) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-20">
      <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6">
        <ShoppingCart className="h-12 w-12 text-gray-300" />
      </div>
      <h2 className="text-2xl font-bold text-navy mb-2">{showPrices ? 'Your cart is empty' : 'Your inquiry list is empty'}</h2>
      <p className="text-muted-foreground mb-8 text-center max-w-md">Add products and come back here to review your {showPrices ? 'order' : 'enquiry'} before proceeding.</p>
      <Link to="/products">
        <Button className="bg-[#0c1b44] text-white hover:bg-[#1a2d64] h-12 px-8 text-base font-semibold">
          Browse Products <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-navy" data-testid="cart-title">{showPrices ? 'Shopping Cart' : 'Inquiry List'}</h1>
            <p className="text-sm text-muted-foreground">{cart.items?.reduce((s, i) => s + i.quantity, 0)} item(s)</p>
          </div>
          <Link to="/products" className="text-sm text-[#0c1b44] hover:underline flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Continue Shopping
          </Link>
        </div>

        {!showPrices && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <MessageSquare className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-amber-800 text-sm">Prices are available upon request. Submit your inquiry and we'll send you a detailed quotation within 24 hours.</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3">
            {cart.items?.map((item) => (
              <div key={item.product_id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex gap-4 p-4">
                  {/* Product Image */}
                  <div
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50 border border-gray-100 cursor-pointer"
                    onClick={() => navigate(`/products/${generateProductSlug(item.product?.name, item.product_id, item.product?.slug)}`)}
                  >
                    <img src={item.product?.images?.[0] || "https://placehold.co/100/f1f5f9/0c1b44?text=IMG"} alt={item.product?.name} className="w-full h-full object-contain p-1" />
                  </div>
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${generateProductSlug(item.product?.name, item.product_id, item.product?.slug)}`} className="font-semibold text-navy hover:text-[#0c1b44] text-sm sm:text-base line-clamp-2 block mb-0.5">{item.product?.name}</Link>
                    {item.product?.brand && <p className="text-xs text-muted-foreground mb-2">{item.product.brand}</p>}
                    {(showPrices || item.unit_price > 1) && <p className="text-[#0c1b44] font-bold"><Price amount={item.unit_price} size="sm" showIcon={true} /></p>}
                  </div>
                  {/* Controls */}
                  <div className="flex flex-col items-end justify-between gap-2">
                    <button onClick={() => removeFromCart(item.product_id)} className="text-gray-300 hover:text-red-500 transition-colors p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                      <button onClick={() => updateCartItem(item.product_id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors">
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-10 text-center text-sm font-semibold text-navy">{item.quantity}</span>
                      <button onClick={() => updateCartItem(item.product_id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors">
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    {(showPrices || item.total_price > 1) && <p className="text-sm font-bold text-navy"><Price amount={item.total_price} size="sm" showIcon={true} /></p>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 sticky top-24">
              <h2 className="font-bold text-navy text-lg mb-5">{showPrices ? 'Order Summary' : 'Inquiry Summary'}</h2>
              <div className="space-y-3 mb-5">
                {showPrices ? (
                  <>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-medium"><Price amount={cart.subtotal} size="sm" showIcon={true} /></span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">VAT ({settings.vat_percentage || 5}%)</span><span className="font-medium"><Price amount={cart.vat} size="sm" showIcon={true} /></span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Shipping</span><span className="font-medium text-green-600">{cart.shipping === 0 ? 'FREE' : `AED ${cart.shipping?.toFixed(2)}`}</span></div>
                    {cart.subtotal < (settings.free_shipping_threshold || 1000) && (
                      <p className="text-xs text-[#0c1b44] bg-blue-50 p-2 rounded-lg">Add <Price amount={(settings.free_shipping_threshold || 1000) - cart.subtotal} size="sm" showIcon={true} /> more for free shipping!</p>
                    )}
                    <Separator />
                    <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="text-[#0c1b44]"><Price amount={cart.total} size="lg" showIcon={true} /></span></div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Items</span><span>{cart.items?.reduce((s, i) => s + i.quantity, 0)} items</span></div>
                    <Separator />
                    <p className="text-xs text-muted-foreground">Pricing will be provided in your quotation</p>
                  </>
                )}
              </div>
              <Button className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white font-bold h-12 text-base" onClick={() => navigate('/checkout')} data-testid="checkout-btn">
                {showPrices ? 'Proceed to Checkout' : 'Request Quotation'} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-xl font-bold text-navy mb-6">You May Also Like</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {relatedProducts.slice(0, 5).map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// Checkout Page
const CheckoutPage = () => {
  const { cart, user, settings, API, getAuthHeaders, sessionId } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Check if user email is verified (required for checkout)
  const emailVerified = user?.email_verified || user?.role === 'admin';

  // Check if prices are hidden (quotation mode) or both payment methods are disabled
  const showPrices = settings?.show_prices !== false;
  const paymentDisabled = !settings.payment_enabled && !settings.cod_enabled;
  const isQuotationMode = !showPrices || paymentDisabled;
  const [paymentMethod, setPaymentMethod] = useState(settings.payment_enabled ? 'card' : (settings.cod_enabled ? 'cod' : 'enquiry'));
  const [formData, setFormData] = useState({ full_name: user?.name || '', email: user?.email || '', phone: user?.phone || '+971', address_line1: '', city: 'Dubai', emirate: 'Dubai', notes: '' });
  const emirates = settings.delivery_emirates || ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check email verification before proceeding
    if (!emailVerified) {
      setShowVerificationModal(true);
      return;
    }

    setLoading(true);
    try {
      if (isQuotationMode) {
        // Submit as order with "quotation" payment method
        const orderData = {
          items: cart.items.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
          shipping_address: {
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone,
            address_line1: formData.address_line1,
            city: formData.city,
            emirate: formData.emirate
          },
          payment_method: 'quotation',
          notes: formData.notes
        };
        const params = sessionId ? { session_id: sessionId } : {};
        const res = await axios.post(`${API}/orders`, orderData, { headers: getAuthHeaders(), params });

        toast.success(showPrices ? 'Enquiry submitted successfully! We will contact you soon.' : 'Quotation request submitted! We will send you pricing shortly.');
        navigate(`/order-confirmation/${res.data.order_id}`);
      } else {
        // Normal order flow
        const orderData = { items: cart.items.map(item => ({ product_id: item.product_id, quantity: item.quantity })), shipping_address: { full_name: formData.full_name, email: formData.email, phone: formData.phone, address_line1: formData.address_line1, city: formData.city, emirate: formData.emirate }, payment_method: paymentMethod, notes: formData.notes };
        const params = sessionId ? { session_id: sessionId } : {};
        const res = await axios.post(`${API}/orders`, orderData, { headers: getAuthHeaders(), params });
        if (res.data.requires_payment && paymentMethod === 'card') {
          const checkoutRes = await axios.post(`${API}/checkout/create-session`, { order_id: res.data.order_id, origin_url: window.location.origin }, { headers: getAuthHeaders() });
          window.location.href = checkoutRes.data.checkout_url;
        } else { toast.success('Order placed successfully!'); navigate(`/order-confirmation/${res.data.order_id}`); }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    setLoading(false);
  };

  if (cart.items?.length === 0) { navigate('/cart'); return null; }

  const steps = [
    { id: 1, label: 'Cart' },
    { id: 2, label: 'Your Details' },
    { id: 3, label: 'Confirm' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white border-b shadow-sm py-4">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-center gap-0">
            {steps.map((step, i) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${step.id <= 2 ? 'bg-[#0c1b44] text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {step.id < 2 ? <Check className="h-3.5 w-3.5" /> : step.id}
                  </div>
                  <span className={`text-sm font-medium hidden sm:block ${step.id <= 2 ? 'text-[#0c1b44]' : 'text-gray-400'}`}>{step.label}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 mx-3 h-0.5 max-w-[80px] ${step.id < 2 ? 'bg-[#0c1b44]' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <h1 className="text-2xl font-bold text-navy mb-2" data-testid="checkout-title">{isQuotationMode ? (!showPrices ? 'Request Quotation' : 'Submit Enquiry') : 'Checkout'}</h1>
        {isQuotationMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-800 flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              <span>{!showPrices ? 'Fill in your details below and we\'ll send you a quotation with pricing.' : 'Fill in your details below. Our team will contact you to confirm your order and arrange payment.'}</span>
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card><CardHeader><CardTitle>Contact Information</CardTitle></CardHeader><CardContent className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Full Name *</Label><Input required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} data-testid="checkout-name" /></div>
                <div className="space-y-2"><Label>Email *</Label><Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="checkout-email" /></div>
                <div className="space-y-2 sm:col-span-2"><Label>Mobile Number *</Label><Input required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+971" data-testid="checkout-phone" /></div>
              </CardContent></Card>
              <Card><CardHeader><CardTitle>Shipping Address</CardTitle></CardHeader><CardContent className="space-y-4">
                <div className="space-y-2"><Label>Address *</Label><Input required value={formData.address_line1} onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })} placeholder="Building, Street" data-testid="checkout-address" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>City *</Label><Input required value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Emirate *</Label><Select value={formData.emirate} onValueChange={(v) => setFormData({ ...formData, emirate: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{emirates.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="space-y-2"><Label>Order Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any special instructions or requirements..." /></div>
              </CardContent></Card>

              {/* Only show payment options if payment is enabled and prices are shown */}
              {!isQuotationMode && (
                <Card><CardHeader><CardTitle>Payment Method</CardTitle></CardHeader><CardContent className="space-y-4">
                  {settings.payment_enabled && <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${paymentMethod === 'card' ? 'border-[#0c1b44] bg-[#0c1b44]/5' : ''}`}><input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={(e) => setPaymentMethod(e.target.value)} className="accent-gold" /><CreditCard className="h-5 w-5 text-[#0c1b44]" /><div><p className="font-medium">Credit/Debit Card</p><p className="text-sm text-muted-foreground">Secure payment via Stripe</p></div></label>}
                  {settings.cod_enabled && <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${paymentMethod === 'cod' ? 'border-[#0c1b44] bg-[#0c1b44]/5' : ''}`}><input type="radio" name="payment" value="cod" checked={paymentMethod === 'cod'} onChange={(e) => setPaymentMethod(e.target.value)} className="accent-gold" /><Truck className="h-5 w-5 text-[#0c1b44]" /><div><p className="font-medium">Cash on Delivery</p><p className="text-sm text-muted-foreground">Pay when you receive</p></div></label>}
                </CardContent></Card>
              )}
            </div>
            <div><Card className="sticky top-24"><CardHeader><CardTitle>{isQuotationMode && !showPrices ? 'Inquiry Summary' : 'Order Summary'}</CardTitle></CardHeader><CardContent className="space-y-4">
              {cart.items?.map((item) => (<div key={item.product_id} className="flex gap-3"><div className="w-16 h-16 rounded overflow-hidden flex-shrink-0"><img src={item.product?.images?.[0] || "https://placehold.co/60/f1f5f9/0c1b44"} alt="" className="w-full h-full object-cover" /></div><div className="flex-1"><p className="text-sm font-medium line-clamp-1">{item.product?.name}</p><p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>{showPrices && <p className="text-sm font-semibold"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{item.total_price?.toFixed(2)}</span></p>}</div></div>))}
              <Separator />
              {showPrices ? (
                <>
                  <div className="space-y-2"><div className="flex justify-between text-sm"><span>Subtotal</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{cart.subtotal?.toFixed(2)}</span></span></div><div className="flex justify-between text-sm"><span>VAT</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{cart.vat?.toFixed(2)}</span></span></div><div className="flex justify-between text-sm"><span>Shipping</span><span>{cart.shipping === 0 ? 'FREE' : `AED ${cart.shipping?.toFixed(2)}`}</span></div></div>
                  <Separator /><div className="flex justify-between text-lg font-bold"><span>Total</span><span className="text-[#0c1b44]"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{cart.total?.toFixed(2)}</span></span></div>
                </>
              ) : (
                <div className="text-center py-2 space-y-3">
                  <p className="text-sm text-muted-foreground">Pricing will be included in your quotation</p>
                  <div className="text-xs text-[#0c1b44] bg-blue-50 p-3 rounded-lg flex flex-col sm:flex-row items-center justify-center gap-2">
                    <Truck className="h-4 w-4 flex-shrink-0" />
                    <span>Free standard shipping on orders above <strong><Price amount={settings?.free_shipping_threshold || 1000} size="sm" showIcon={true} /></strong></span>
                  </div>
                </div>
              )}
              <Button type="submit" className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white font-bold h-12" disabled={loading} data-testid="place-order-btn">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (isQuotationMode ? <><Send className="h-5 w-5 mr-2" /> {!showPrices ? 'Request Quotation' : 'Submit Enquiry'}</> : 'Place Order')}
              </Button>
              {isQuotationMode && <p className="text-center text-sm text-muted-foreground">{!showPrices ? 'We\'ll send you pricing within 24 hours' : 'Our team will contact you to confirm the order'}</p>}
            </CardContent></Card></div>
          </div>
        </form>
      </div>

      {/* Email Verification Required Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <Mail className="h-8 w-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2">Email Verification Required</h3>
              <p className="text-muted-foreground mb-6">Please verify your email address before placing an order. This helps us confirm your identity and send order updates.</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => setShowVerificationModal(false)}>Cancel</Button>
                <Button className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white" onClick={() => navigate('/verify-account')}>Verify Email</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Order Confirmation
const OrderConfirmationPage = () => {
  const { id } = useParams();
  const { getAuthHeaders, API } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => { try { const res = await axios.get(`${API}/orders/${id}`, { headers: getAuthHeaders() }); setOrder(res.data); } catch (e) { console.error('Failed to fetch order'); } setLoading(false); };
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, API]);

  if (loading) return <div className="container mx-auto px-4 py-16 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;
  if (!order) return <div className="container mx-auto px-4 py-16 text-center"><h2 className="text-2xl font-bold">Order not found</h2><Link to="/"><Button className="mt-4 bg-[#0c1b44] text-white hover:bg-[#1a2d64]">Go Home</Button></Link></div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"><Check className="h-10 w-10 text-green-600" /></div>
          <h1 className="text-3xl font-bold text-navy mb-2">Thank You!</h1>
          <p className="text-muted-foreground mb-6">Your order has been placed successfully</p>
          <Card className="text-left"><CardContent className="p-6 space-y-4">
            <div className="flex justify-between"><span className="text-muted-foreground">Order Number</span><span className="font-bold text-[#0c1b44]">{order.order_number}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge className="bg-[#0c1b44]/10 text-[#0c1b44]">{order.status?.toUpperCase()}</Badge></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span>{order.payment_method === 'cod' ? 'Cash on Delivery' : order.payment_status}</span></div>
            <Separator /><div className="flex justify-between text-lg font-bold"><span>Total</span><span className="text-[#0c1b44]"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{order.total?.toFixed(2)}</span></span></div>
          </CardContent></Card>
          <div className="mt-6 flex gap-4 justify-center"><Link to="/products"><Button variant="outline">Continue Shopping</Button></Link></div>
        </div>
      </div>
    </div>
  );
};

// Checkout Success
const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { API } = useApp();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) { navigate('/'); return; }
      try { const res = await axios.get(`${API}/checkout/status/${sessionId}`); setStatus(res.data.payment_status === 'paid' ? 'success' : 'pending'); } catch (e) { setStatus('error'); }
    };
    checkStatus(); const interval = setInterval(checkStatus, 2000); setTimeout(() => clearInterval(interval), 10000); return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, API, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        {status === 'checking' && <><Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#0c1b44]" /><h2 className="text-xl font-semibold">Verifying payment...</h2></>}
        {status === 'success' && <><div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"><Check className="h-10 w-10 text-green-600" /></div><h2 className="text-2xl font-bold text-navy mb-2">Payment Successful!</h2><Link to="/"><Button className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]">Go Home</Button></Link></>}
        {status === 'pending' && <><AlertCircle className="h-12 w-12 mx-auto mb-4 text-orange-500" /><h2 className="text-xl font-semibold">Payment Pending</h2></>}
        {status === 'error' && <><X className="h-12 w-12 mx-auto mb-4 text-red-500" /><h2 className="text-xl font-semibold">Payment Failed</h2><Link to="/cart"><Button className="mt-4">Return to Cart</Button></Link></>}
      </div>
    </div>
  );
};

// Enquiry Confirmation Page
const EnquiryConfirmationPage = () => {
  const { id } = useParams();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <Check className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-navy mb-4">Enquiry Submitted!</h1>
          <p className="text-muted-foreground mb-6">Thank you for your enquiry. Our team will contact you shortly to confirm your order and arrange payment.</p>

          <Card className="text-left mb-6">
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Enquiry Reference</span>
                <span className="font-bold text-[#0c1b44]">{id}</span>
              </div>
              <Separator />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  What Happens Next?
                </h3>
                <ul className="text-blue-700 text-sm space-y-2">
                  <li>• Our team will review your enquiry within 24 hours</li>
                  <li>• We'll contact you via phone or email to confirm details</li>
                  <li>• Payment can be arranged via bank transfer or other methods</li>
                  <li>• Your order will be processed once payment is confirmed</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4 justify-center">
            <Link to="/products">
              <Button variant="outline">Continue Shopping</Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white">Contact Us</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};


// Login Page
const LoginPage = () => {
  const { login, user, setUser, API } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Phone Auth State
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [phone, setPhone] = useState('+971 ');
  const [otp, setOtp] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);

  useEffect(() => {
    // Check for session_id from Emergent Managed OAuth
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', '?'));
    const sessionId = params.get('session_id') || searchParams.get('session_id');

    if (sessionId) {
      const exchangeSession = async () => {
        setLoading(true);
        try {
          const res = await axios.post(`${API}/auth/emergent/session`, { session_id: sessionId });
          if (res.data.token && res.data.user) {
            localStorage.setItem('token', res.data.token);
            setUser(res.data.user);
            toast.success('Login successful!');
            navigate(res.data.user.role === 'admin' ? '/admin' : '/account');
          }
        } catch (err) {
          console.error('Emergent session exchange failed:', err);
          toast.error('Google login failed. Please try again.');
        }
        setLoading(false);
      };
      exchangeSession();
      return;
    }

    // Check for error from Google OAuth
    const error = searchParams.get('error');
    if (error) {
      toast.error('Google login failed. Please try again.');
    }

    if (user) {
      if (!user.email_verified && user.role !== 'admin' && user.auth_provider !== 'google' && !user.phone_verified) {
        navigate('/verify-account');
      } else {
        navigate(user.role === 'admin' ? '/admin' : '/account');
      }
    }
  }, [user, navigate, searchParams, API, setUser]);

  useEffect(() => {
    if (searchParams.get('phone') === 'true') {
      setShowPhoneLogin(true);
    }
  }, [searchParams]);

  // Phone login effect handled by URL params
  useEffect(() => {
    if (searchParams.get('phone') === 'true') {
      setShowPhoneLogin(true);
    }
  }, [searchParams]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setPhoneLoading(true);
    try {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          'size': 'invisible',
          'callback': (response) => { }
        });
      }
      const appVerifier = window.recaptchaVerifier;
      const confirmationResult = await signInWithPhoneNumber(auth, phone, appVerifier);
      window.confirmationResult = confirmationResult;
      setVerificationId(confirmationResult.verificationId);
      toast.success('OTP sent to your phone!');
    } catch (err) {
      console.error('OTP Send Error:', err);
      toast.error('Failed to send OTP. Please try again.');
    }
    setPhoneLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setPhoneLoading(true);
    try {
      const result = await window.confirmationResult.confirm(otp);
      const idToken = await result.user.getIdToken();
      
      // Exchange Firebase token for our backend JWT
      const res = await axios.post(`${API}/auth/firebase/verify`, { idToken });
      
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Verification successful!');
        navigate(res.data.user.role === 'admin' ? '/admin' : '/account');
      }
    } catch (err) {
      console.error('OTP Verify Error:', err);
      toast.error('Invalid OTP. Please try again.');
    }
    setPhoneLoading(false);
  };

  const [admin2fa, setAdmin2fa] = useState({ step: null, preSessionId: null, phoneChoices: [], sessionId: null, maskedPhone: '' });

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      // First try regular login
      const res = await login(email, password);
      toast.success('Welcome back!');
      if (!res.user.email_verified && res.user.role !== 'admin' && res.user.auth_provider !== 'google') {
        navigate('/verify-account');
      } else {
        navigate(res.user.role === 'admin' ? '/admin' : '/account');
      }
    } catch (err) { 
      // If blocked because it's an admin, try admin login
      if (err.response?.status === 403 && err.response?.data?.detail?.includes('admin portal')) {
        try {
          const adminRes = await adminLogin(email, password);
          if (adminRes.requires_2fa) {
            setAdmin2fa({
              step: 'select_phone',
              preSessionId: adminRes.pre_session_id,
              phoneChoices: adminRes.phone_choices,
              sessionId: null,
              maskedPhone: ''
            });
            toast.info('Security verification required');
          }
        } catch (adminErr) {
          toast.error(adminErr.response?.data?.detail || 'Admin login failed');
        }
      } else {
        toast.error(err.response?.data?.detail || 'Login failed'); 
      }
    }
    setLoading(false);
  };

  const handleAdminSelectPhone = async (phoneIndex) => {
    setLoading(true);
    try {
      const res = await adminSend2FA(admin2fa.preSessionId, phoneIndex);
      setAdmin2fa({
        ...admin2fa,
        step: 'enter_otp',
        sessionId: res.session_id,
        maskedPhone: res.masked_phone
      });
      toast.success('Verification code sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    }
    setLoading(false);
  };

  const handleAdminVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminVerify2FA(admin2fa.sessionId, otp);
      toast.success('Welcome back, Admin!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      
      // Exchange Firebase token for our backend JWT
      const res = await axios.post(`${API}/auth/firebase/google`, { idToken });
      
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Welcome! Logged in with Google.');
        navigate(res.data.user.role === 'admin' ? '/admin' : '/account');
      }
    } catch (err) {
      console.error('Google login failed:', err);
      toast.error('Google login failed. Please try again.');
    }
    setGoogleLoading(false);
  };

  // ── Forgot Password State ─────────────────────────────────────
  const [forgotStep, setForgotStep] = useState(null); // null | 'email' | 'otp' | 'reset' | 'done'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      if (res.data.google_account) {
        toast.info('This account uses Google Sign-In. Please login with Google.');
        setForgotStep(null);
      } else {
        toast.success('Verification code sent to your email!');
        setForgotStep('otp');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset code.');
    }
    setForgotLoading(false);
  };

  const handleForgotVerifyOtp = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password/verify`, { email: forgotEmail, otp: forgotOtp });
      setResetToken(res.data.reset_token);
      toast.success('Code verified! Set your new password.');
      setForgotStep('reset');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code.');
    }
    setForgotLoading(false);
  };

  const handleForgotResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/reset`, { reset_token: resetToken, new_password: newPassword });
      toast.success('Password reset successfully! Please login.');
      setForgotStep(null);
      setForgotEmail('');
      setForgotOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password.');
    }
    setForgotLoading(false);
  };

  const resetForgotState = () => {
    setForgotStep(null);
    setForgotEmail('');
    setForgotOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setResetToken('');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-navy">Welcome Back</CardTitle>
          <p className="text-muted-foreground">Login to your account</p>
        </CardHeader>
        <CardContent className="pt-6">
          {admin2fa.step === 'select_phone' ? (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <Shield className="h-10 w-10 text-navy mx-auto mb-2" />
                <h3 className="font-bold text-lg">2-Step Verification</h3>
                <p className="text-sm text-muted-foreground">Select a phone number to receive a verification code</p>
              </div>
              <div className="grid gap-2">
                {admin2fa.phoneChoices.map((choice) => (
                  <Button 
                    key={choice.index} 
                    variant="outline" 
                    className="h-14 justify-between px-4 border-2 hover:border-navy hover:bg-navy/5"
                    onClick={() => handleAdminSelectPhone(choice.index)}
                    disabled={loading}
                  >
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium text-navy">{choice.masked}</span>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </Button>
                ))}
              </div>
              <Button 
                variant="link" 
                className="w-full text-muted-foreground"
                onClick={() => setAdmin2fa({ step: null, preSessionId: null, phoneChoices: [], sessionId: null, maskedPhone: '' })}
              >
                Cancel and back to login
              </Button>
            </div>
          ) : admin2fa.step === 'enter_otp' ? (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <Shield className="h-10 w-10 text-navy mx-auto mb-2" />
                <h3 className="font-bold text-lg">Enter Verification Code</h3>
                <p className="text-sm text-muted-foreground">Sent to <span className="font-semibold text-navy">{admin2fa.maskedPhone}</span></p>
              </div>
              <form onSubmit={handleAdminVerifyOtp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-otp">Verification Code</Label>
                  <Input 
                    id="admin-otp" 
                    type="text" 
                    required 
                    value={otp} 
                    onChange={(e) => setOtp(e.target.value)} 
                    placeholder="000000" 
                    className="h-14 text-center text-3xl font-bold tracking-[0.5em]" 
                    maxLength={6} 
                  />
                </div>
                <Button type="submit" className="w-full bg-navy hover:bg-navy-dark text-white h-12 font-bold shadow-sm" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Sign In'}
                </Button>
                <Button 
                  type="button"
                  variant="link" 
                  className="w-full text-muted-foreground"
                  onClick={() => setAdmin2fa({ ...admin2fa, step: 'select_phone' })}
                >
                  Back to phone selection
                </Button>
              </form>
            </div>
          ) : forgotStep === 'email' ? (
            <form onSubmit={handleForgotSendOtp} className="space-y-4">
              <div className="text-center mb-2">
                <div className="w-12 h-12 bg-[#0c1b44]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="h-6 w-6 text-[#0c1b44]" />
                </div>
                <h3 className="font-semibold text-[#0c1b44] text-lg">Forgot Password</h3>
                <p className="text-sm text-muted-foreground mt-1">Enter your email to receive a reset code</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input id="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="name@example.com" className="h-11" autoFocus />
              </div>
              <Button type="submit" className="w-full bg-navy hover:bg-navy-dark text-white h-11 font-semibold" disabled={forgotLoading}>
                {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Code'}
              </Button>
              <div className="text-center">
                <Button type="button" variant="link" className="text-[#0c1b44] hover:text-[#1a2d64] p-0 h-auto font-medium" onClick={resetForgotState}>← Back to Login</Button>
              </div>
            </form>
          ) : forgotStep === 'otp' ? (
            <form onSubmit={handleForgotVerifyOtp} className="space-y-4">
              <div className="text-center mb-2">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-[#0c1b44] text-lg">Verify Code</h3>
                <p className="text-sm text-muted-foreground mt-1">Enter the 6-digit code sent to <span className="font-semibold text-[#0c1b44]">{forgotEmail}</span></p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="forgot-otp">Verification Code</Label>
                <Input id="forgot-otp" type="text" required value={forgotOtp} onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="h-14 text-center text-3xl font-bold tracking-[0.5em]" maxLength={6} autoFocus />
              </div>
              <Button type="submit" className="w-full bg-navy hover:bg-navy-dark text-white h-11 font-semibold" disabled={forgotLoading || forgotOtp.length < 6}>
                {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify Code'}
              </Button>
              <div className="text-center">
                <Button type="button" variant="link" className="text-[#0c1b44] hover:text-[#1a2d64] p-0 h-auto font-medium" onClick={() => setForgotStep('email')}>← Change Email</Button>
              </div>
            </form>
          ) : forgotStep === 'reset' ? (
            <form onSubmit={handleForgotResetPassword} className="space-y-4">
              <div className="text-center mb-2">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Lock className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-[#0c1b44] text-lg">Set New Password</h3>
                <p className="text-sm text-muted-foreground mt-1">Choose a strong password for your account</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input id="new-password" type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimum 6 characters" className="h-11" autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input id="confirm-password" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" className="h-11" />
              </div>
              <Button type="submit" className="w-full bg-navy hover:bg-navy-dark text-white h-11 font-semibold" disabled={forgotLoading || newPassword.length < 6}>
                {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
              </Button>
              <div className="text-center">
                <Button type="button" variant="link" className="text-muted-foreground p-0 h-auto" onClick={resetForgotState}>Cancel</Button>
              </div>
            </form>
          ) : !showPhoneLogin ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="h-11" data-testid="login-email" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button type="button" className="text-xs text-[#0c1b44] hover:underline font-medium" onClick={() => setForgotStep('email')}>Forgot Password?</button>
                </div>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password" />
              </div>
              <Button type="submit" className="w-full bg-navy hover:bg-navy-dark text-white h-11 font-semibold" disabled={loading} data-testid="login-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
              </Button>
              <div className="text-center">
                <Button type="button" variant="link" className="text-[#0c1b44] hover:text-[#1a2d64] p-0 h-auto font-medium" onClick={() => setShowPhoneLogin(true)}>
                  Sign in with Phone Number
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {!verificationId ? (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+971" className="h-11 text-lg font-medium tracking-tight" />
                  </div>
                  <Button type="submit" className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-11 font-bold shadow-sm" disabled={phoneLoading}>
                    {phoneLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send OTP Code'}
                  </Button>
                  <div className="text-center">
                    <Button type="button" variant="link" className="text-navy hover:text-navy-light p-0 h-auto font-medium" onClick={() => setShowPhoneLogin(false)}>
                      Back to Email Login
                    </Button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="space-y-2 text-center mb-4">
                    <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to <span className="font-semibold text-navy">{phone}</span></p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="otp">Verification Code</Label>
                    <Input id="otp" type="text" required value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="000000" className="h-12 text-center text-2xl font-bold tracking-[0.5em]" maxLength={6} />
                  </div>
                  <Button type="submit" className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-11 font-bold shadow-sm" disabled={phoneLoading}>
                    {phoneLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Continue'}
                  </Button>
                  <div className="text-center">
                    <Button type="button" variant="link" className="text-navy hover:text-navy-light p-0 h-auto font-medium" onClick={() => setVerificationId(null)}>
                      Edit Phone Number
                    </Button>
                  </div>
                </form>
              )}
              <div id="recaptcha-container" className="flex justify-center my-4"></div>
            </div>
          )}

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">Or continue with</span></div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 border-2"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            data-testid="google-login-btn"
          >
            {googleLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </>
            )}
          </Button>

          <div className="mt-4 text-center text-sm"><span className="text-muted-foreground">Don't have an account? </span><Link to="/register" className="text-[#0c1b44] hover:underline">Register</Link></div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Portal Login — Hidden secure admin login with 2FA
const AdminPortalLogin = () => {
  const { setUser, API } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('login'); // 'login' | 'select_phone' | '2fa'
  const [preSessionId, setPreSessionId] = useState('');
  const [phoneChoices, setPhoneChoices] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/admin-login`, { email, password });
      
      if (res.data.requires_2fa && res.data.step === 'select_phone') {
        // Step 2: Show phone selection
        setPreSessionId(res.data.pre_session_id);
        setPhoneChoices(res.data.phone_choices || []);
        setStep('select_phone');
        toast.success('Password verified! Select your phone.');
      } else if (res.data.token && res.data.user) {
        // Direct login (fallback)
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Welcome back, Admin!');
        navigate('/admin');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      toast.error(detail);
    }
    setLoading(false);
  };

  const handleSendOTP = async () => {
    if (selectedPhone === null) {
      toast.error('Please select a phone number');
      return;
    }
    setPhoneLoading(true);
    try {
      const res = await axios.post(`${API}/auth/admin-send-2fa`, {
        pre_session_id: preSessionId,
        phone_index: selectedPhone
      });
      
      if (res.data.requires_2fa && res.data.step === 'enter_otp') {
        setSessionId(res.data.session_id);
        setMaskedPhone(res.data.masked_phone);
        setStep('2fa');
        toast.success(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
    setPhoneLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API}/auth/admin-verify-2fa`, {
        session_id: sessionId,
        otp: otp
      });
      
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Welcome back, Admin!');
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    }
    setOtpLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0c1b44] to-[#1a2d64] flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-gradient-to-br from-[#d4af37] to-[#f0d78c] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="h-8 w-8 text-[#0c1b44]" />
          </div>
          <CardTitle className="text-2xl text-[#0c1b44]">Admin Portal</CardTitle>
          <p className="text-muted-foreground text-sm">Secure administrative access</p>
        </CardHeader>
        <CardContent className="pt-4">
          {step === 'login' ? (
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@gpgt.ae"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-11 font-semibold"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
              </Button>
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-4">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700">This portal is for authorized administrators only. All login attempts are monitored.</p>
              </div>
            </form>
          ) : step === 'select_phone' ? (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-[#0c1b44] mb-1">Select Phone for OTP</h3>
                <p className="text-sm text-muted-foreground">
                  Choose which phone number should receive the verification code
                </p>
              </div>
              <div className="space-y-2">
                {phoneChoices.map((phone) => (
                  <label
                    key={phone.index}
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedPhone === phone.index
                        ? 'border-[#0c1b44] bg-[#0c1b44]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedPhone(phone.index)}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedPhone === phone.index ? 'border-[#0c1b44]' : 'border-gray-300'
                    }`}>
                      {selectedPhone === phone.index && (
                        <div className="w-3 h-3 rounded-full bg-[#0c1b44]" />
                      )}
                    </div>
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-mono font-semibold text-[#0c1b44]">{phone.masked}</span>
                  </label>
                ))}
              </div>
              <Button
                onClick={handleSendOTP}
                className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-11 font-semibold"
                disabled={phoneLoading || selectedPhone === null}
              >
                {phoneLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send OTP'}
              </Button>
              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  className="text-muted-foreground hover:text-[#0c1b44] p-0 h-auto text-sm"
                  onClick={() => { setStep('login'); setSelectedPhone(null); }}
                >
                  ← Back to login
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-sm text-muted-foreground">
                  A verification code has been sent to<br />
                  <span className="font-semibold text-[#0c1b44]">{maskedPhone}</span>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-otp">Verification Code</Label>
                <Input
                  id="admin-otp"
                  type="text"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="0000"
                  className="h-14 text-center text-3xl font-bold tracking-[0.5em]"
                  maxLength={4}
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-11 font-semibold"
                disabled={otpLoading || otp.length < 4}
              >
                {otpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Login'}
              </Button>
              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  className="text-muted-foreground hover:text-[#0c1b44] p-0 h-auto text-sm"
                  onClick={() => { setStep('login'); setOtp(''); setSelectedPhone(null); }}
                >
                  ← Back to login
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const { register, user, API } = useApp();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    // If user is logged in but not verified, go to verification
    if (user && !user.email_verified && user.auth_provider !== 'google') {
      navigate('/verify-account');
    } else if (user) {
      navigate('/account');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const response = await register(formData);
      toast.success('Account created! Please verify your email.');
      // Redirect to verification page
      navigate('/verify-account');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      
      // Exchange Firebase token for our backend JWT
      const res = await axios.post(`${API}/auth/firebase/google`, { idToken });
      
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Welcome! Account created with Google.');
        navigate(res.data.user.role === 'admin' ? '/admin' : '/account');
      }
    } catch (e) {
      console.error('Google signup failed:', e);
      toast.error(e.response?.data?.detail || 'Google signup failed. Please try again.');
    }
    setGoogleLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center"><CardTitle className="text-2xl text-navy">Create Account</CardTitle></CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 border-2 mb-4"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            data-testid="google-register-btn"
          >
            {googleLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Sign up with Google
              </>
            )}
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">Or register with email</span></div>
          </div>

          <div className="mb-4">
            <Button variant="ghost" className="w-full text-[#0c1b44]" onClick={() => navigate('/login?phone=true')}>Signup with Phone Number</Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="register-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="register-email" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" required value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} data-testid="register-password" /></div>
            <Button type="submit" className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white" disabled={loading} data-testid="register-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Register'}</Button>
          </form>
          <p className="text-xs text-center text-muted-foreground mt-3">By registering, you'll receive a verification code via email.</p>
          <div className="mt-4 text-center text-sm"><span className="text-muted-foreground">Already have an account? </span><Link to="/login" className="text-[#0c1b44] hover:underline">Login</Link></div>
        </CardContent>
      </Card>
    </div>
  );
};

// Auth Callback Page (handles Google OAuth redirect)
const AuthCallbackPage = () => {
  const { setUser, API } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for error
      const error = searchParams.get('error');
      if (error) {
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
        return;
      }

      // Check for Emergent OAuth session_id in URL fragment
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];

        if (sessionId) {
          try {
            // Exchange session_id for token via backend
            const res = await axios.post(`${API}/auth/emergent/session`, {
              session_id: sessionId
            });

            if (res.data.token && res.data.user) {
              localStorage.setItem('token', res.data.token);
              setUser(res.data.user);
              toast.success('Welcome! You are now logged in with Google.');

              // Clear the hash from URL
              window.history.replaceState(null, '', window.location.pathname);

              navigate(res.data.user.role === 'admin' ? '/admin' : '/account');
              return;
            }
          } catch (e) {
            console.error('Emergent auth failed', e);
            toast.error(e.response?.data?.detail || 'Google login failed');
            navigate('/login');
            return;
          }
        }
      }

      // Check for legacy token in query params (backward compatibility)
      const token = searchParams.get('token');
      if (token) {
        localStorage.setItem('token', token);

        try {
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(res.data);
          toast.success('Welcome! You are now logged in.');
          navigate(res.data.role === 'admin' ? '/admin' : '/account');
        } catch (e) {
          console.error('Failed to fetch user', e);
          toast.error('Failed to complete login');
          navigate('/login');
        }
        return;
      }

      // No auth data found
      navigate('/login');
    };

    processAuth();
  }, [searchParams, navigate, setUser, API]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#0c1b44]" />
        <h2 className="text-xl font-semibold text-navy">Completing login...</h2>
        <p className="text-muted-foreground">Please wait while we verify your account.</p>
      </div>
    </div>
  );
};

// Admin Page — Rich Tracking Dashboard
const AdminPage = () => {
  const { user, API, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchAll = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/dashboard/recent-orders`, { headers: getAuthHeaders() }),
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data || []);
    } catch (e) { console.error('Dashboard fetch failed', e); }
    setLoadingOrders(false);
  };

  if (!user || user.role !== 'admin') return null;

  const statusColor = (s) => {
    const m = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-indigo-100 text-indigo-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      new: 'bg-cyan-100 text-cyan-800',
      invoiced: 'bg-teal-100 text-teal-800',
    };
    return m[s] || 'bg-gray-100 text-gray-700';
  };

  const payColor = (s) => {
    const m = { paid: 'text-green-600', pending: 'text-orange-500', unpaid: 'text-red-500', cod: 'text-blue-600' };
    return m[s] || 'text-gray-500';
  };

  // KPI cards — top row
  const kpis = stats ? [
    { label: 'Total Products', value: stats.total_products ?? 0, icon: Package, color: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600', link: '/admin/products', badge: null },
    { label: 'Total Orders', value: stats.total_orders ?? 0, icon: ShoppingCart, color: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-600', link: '/admin/orders', badge: null },
    { label: 'Pending Orders', value: stats.pending_orders ?? 0, icon: Clock, color: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600', link: '/admin/orders', badge: stats.pending_orders > 0 ? 'Action needed' : null },
    { label: 'Total Customers', value: stats.total_customers ?? 0, icon: Users, color: 'bg-teal-500', light: 'bg-teal-50', text: 'text-teal-600', link: '/admin/customers', badge: null },
    { label: 'Total Revenue', value: `AED ${(stats.total_revenue ?? 0).toFixed(2)}`, icon: DollarSign, color: 'bg-green-500', light: 'bg-green-50', text: 'text-green-600', link: '/admin/erp/reports', badge: null },
  ] : [];

  // Module quick-action cards — grouped by category
  const modules = [
    {
      group: '🛍️ Catalog',
      color: 'border-blue-200',
      items: [
        { to: '/admin/products', icon: Package, label: 'Products', sub: 'Add / edit products', color: 'bg-blue-50 text-blue-600' },
        { to: '/admin/bulk-upload', icon: Upload, label: 'Bulk Upload', sub: 'Import from Excel', color: 'bg-blue-50 text-blue-600' },
        { to: '/admin/categories', icon: Grid, label: 'Categories', sub: 'Manage categories', color: 'bg-blue-50 text-blue-600' },
        { to: '/admin/featured', icon: Star, label: 'Featured', sub: 'Homepage highlights', color: 'bg-blue-50 text-blue-600' },
      ]
    },
    {
      group: '📦 Orders & Sales',
      color: 'border-violet-200',
      items: [
        { to: '/admin/orders', icon: ShoppingCart, label: 'Orders', sub: 'Track & update orders', color: 'bg-violet-50 text-violet-600' },
        { to: '/admin/enquiries', icon: MessageSquare, label: 'Enquiries', sub: 'Quotation requests', color: 'bg-violet-50 text-violet-600' },
        { to: '/admin/erp/invoices', icon: FileText, label: 'Invoices', sub: 'Generate & send', color: 'bg-violet-50 text-violet-600' },
      ]
    },
    {
      group: '📊 Analytics',
      color: 'border-green-200',
      items: [
        { to: '/admin/erp/reports', icon: BarChart2, label: 'Reports', sub: 'Sales analytics', color: 'bg-green-50 text-green-600' },
        { to: '/admin/customers', icon: Users, label: 'Customers', sub: 'User accounts', color: 'bg-green-50 text-green-600' },
        { to: '/admin/settings', icon: Settings, label: 'Settings', sub: 'Store configuration', color: 'bg-green-50 text-green-600' },
      ]
    },
  ];

  return (
    <AdminLayout title="Dashboard" subtitle={`Welcome back, ${user?.name || 'Admin User'} 👋`}>

      {/* ── KPI Strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {stats ? kpis.map(({ label, value, icon: Icon, color, light, text, link, badge }) => (
          <Link to={link} key={label}>
            <div className="bg-white rounded-xl shadow-sm border hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex flex-col gap-3 cursor-pointer">
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-xl ${light} flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 ${text}`} />
                </div>
                {badge && <span className="text-[10px] font-semibold bg-orange-100 text-orange-700 rounded-full px-2 py-0.5">{badge}</span>}
              </div>
              <div>
                <p className={`text-xl font-black ${text}`}>{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </div>
            </div>
          </Link>
        )) : Array(5).fill(0).map((_, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border p-4 h-24 animate-pulse" />
        ))}
      </div>

      {/* ── Main body: recent orders (left) + quick actions (right) ── */}
      <div className="grid lg:grid-cols-3 gap-6">

        {/* Recent Orders Table */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div>
              <h2 className="font-bold text-navy text-base">Recent Orders</h2>
              <p className="text-xs text-muted-foreground">Latest customer order activity</p>
            </div>
            <Link to="/admin/orders">
              <button className="text-xs font-semibold text-[#0c1b44] hover:underline flex items-center gap-1 whitespace-nowrap flex-shrink-0">
                View All <ArrowRight className="h-3 w-3" />
              </button>
            </Link>
          </div>
          <div className="overflow-x-auto">
            {loadingOrders ? (
              <div className="p-6 space-y-3">
                {[1,2,3,4,5].map(i => <div key={i} className="h-10 rounded bg-gray-100 animate-pulse" />)}
              </div>
            ) : recentOrders.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No orders yet</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Order #</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Amount</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Payment</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.slice(0, 8).map((order) => (
                    <tr
                      key={order.id}
                      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/orders?highlight=${order.id}`)}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-[#0c1b44] font-semibold">
                        {order.order_number?.split('-').slice(-1)[0] || order.id?.slice(0,8)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 max-w-[120px] truncate">
                        {order.customer_name || order.shipping_address?.full_name || order.customer_email?.split('@')[0] || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-navy">
                        <span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{(order.total || 0).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${statusColor(order.status)}`}>
                          {order.status || 'new'}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-[10px] font-semibold capitalize ${payColor(order.payment_status)}`}>
                        {order.payment_status || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Quick Action Cards — grouped */}
        <div className="space-y-4">
          {modules.map(({ group, color, items }) => (
            <div key={group} className={`bg-white rounded-xl shadow-sm border-l-4 ${color} border border-gray-100 overflow-hidden`}>
              <p className="text-xs font-bold text-muted-foreground px-4 pt-3 pb-2 uppercase tracking-wider">{group}</p>
              <div className="divide-y divide-gray-50">
                {items.map(({ to, icon: Icon, label, sub, color: ic }) => (
                  <Link to={to} key={to}>
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group">
                      <div className={`w-8 h-8 rounded-lg ${ic} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-navy leading-tight">{label}</p>
                        <p className="text-xs text-muted-foreground truncate">{sub}</p>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-[#0c1b44] transition-colors flex-shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

    </AdminLayout>
  );
};



// Scroll To Top on Route Change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const prevPathRef = React.useRef(pathname);
  React.useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;
    // Don't scroll when navigating between product detail pages —
    // the ProductDetailPage handles its own scroll behavior
    if (prev.startsWith('/products/') && pathname.startsWith('/products/') && prev !== '/products/' && pathname !== '/products/') {
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Main App
function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppProvider>
          <Routes>
            {/* ── Standalone full-screen landing page (no app chrome) ── */}
            <Route path="/new-landing" element={<LandingPage />} />

            {/* ── Main app shell for all other routes ── */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex flex-col">
                  <Header />
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/products/:id" element={<ProductDetailPage />} />
                    <Route path="/catalog" element={<CatalogPage />} />
                    <Route path="/wishlist" element={<WishlistPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                    <Route path="/checkout/cancel" element={<CartPage />} />
                    <Route path="/order-confirmation/:id" element={<OrderConfirmationPage />} />
                    <Route path="/enquiry-confirmation/:id" element={<EnquiryConfirmationPage />} />
                    <Route path="/gpgt-portal" element={<AdminPortalLogin />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/verify-account" element={<VerifyAccountPage />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/industries" element={<IndustriesPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/account" element={<AccountPage />} />
                    <Route path="/order/:id" element={<OrderDetailPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/admin/products" element={<AdminProducts />} />
                    <Route path="/admin/orders" element={<AdminOrders />} />
                    <Route path="/admin/categories" element={<AdminCategories />} />
                    <Route path="/admin/customers" element={<AdminCustomers />} />
                    <Route path="/admin/featured" element={<AdminFeaturedProducts />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/offers" element={<AdminOffers />} />
                    <Route path="/admin/help" element={<AdminHelp />} />
                    <Route path="/admin/chat" element={<AdminChat />} />
                    <Route path="/admin/erp/invoices" element={<InvoiceManagement />} />
                    <Route path="/admin/erp/reports" element={<ReportsModule />} />
                    <Route path="/admin/erp/settings" element={<ERPSettings />} />
                    <Route path="/admin/erp/customers" element={<CustomerERPView />} />
                    <Route path="/admin/enquiries" element={<EnquiriesPage />} />
                    <Route path="/admin/quotations" element={<AdminQuotations />} />
                    <Route path="/admin/bulk-upload" element={<AdminBulkUpload />} />
                    <Route path="*" element={<HomePage />} />
                  </Routes>
                  <Footer />
                  <BottomNav />
                  <WhatsAppButton />
                  <ChatWidget />
                  <Toaster position="top-right" richColors />
                </div>
              }
            />
          </Routes>
        </AppProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
